import React, { useEffect, useRef, useState } from 'react';
import { Message, User, SenderType } from '../messages.types';
import { useAuthStore } from '@/zustand/auth/store.ts';

interface MessageContentProps {
	messages: Message[];
	currentUser: User;
	onReply?: () => void;
	onLoadMore?: () => void;
	hasMore?: boolean;
	isLoading?: boolean;
}

const SENDER_TYPE_LABEL: Record<string, string> = {
	EXPERT: 'Uzman',
	STUDENT: 'Öğrenci',
	SUPPORT: 'Backoffice Yetkilisi'
};

const MessageContentSupport: React.FC<MessageContentProps> = ({ messages, onLoadMore, hasMore = false, isLoading = false, currentUser }) => {
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [prevMessageCount, setPrevMessageCount] = useState(0);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const { data: authData } = useAuthStore();

	const handleLoadMore = () => {
		if (onLoadMore) {
			setIsLoadingMore(true);
			onLoadMore();
		}
	};

	useEffect(() => {
		if (isLoadingMore && messages.length > prevMessageCount) {
			setIsLoadingMore(false);

			const newMessagesCount = messages.length - prevMessageCount;

			if (messagesContainerRef.current && newMessagesCount > 0) {
				const firstMessageElement = messagesContainerRef.current.querySelector('.message-wrapper');
				if (firstMessageElement) {
					setTimeout(() => {
						firstMessageElement.scrollIntoView({ block: 'center' });
					}, 100);
				}
			}
		} else if (!isLoadingMore && messages.length > prevMessageCount) {
			if (messagesEndRef.current) {
				messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
			}
		}

		setPrevMessageCount(messages.length);
	}, [messages, isLoadingMore, prevMessageCount]);

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		const today = new Date();
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);

		if (date.toDateString() === today.toDateString()) {
			return 'Bugün';
		}
		if (date.toDateString() === yesterday.toDateString()) {
			return 'Dün';
		}

		return date.toLocaleDateString('tr-TR', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	};

	const groupedMessages: { [key: string]: Message[] } = {};

	messages.forEach(message => {
		const date = message.date;
		if (!groupedMessages[date]) {
			groupedMessages[date] = [];
		}
		groupedMessages[date].push(message);
	});

	return (
		<div className="chat-content-support">
			{hasMore && (
				<div className="load-more-container">
					<button
						className="load-more-button"
						onClick={handleLoadMore}
						disabled={isLoading}>
						<i className="icons-dialog"></i>
						Önceki Konuşmalarım
					</button>
				</div>
			)}

			{isLoading && (
				<div className="loading-indicator">
					<div className="spinner"></div>
					<span>Yükleniyor...</span>
				</div>
			)}

			{Object.keys(groupedMessages).map(date => (
				<React.Fragment key={date}>
					<div className="message-date-divider">
						<span>{formatDate(date)}</span>
					</div>

					{groupedMessages[date].map(message => {
						const isOutgoing = message.sender === SenderType.USER;

						return (
							<div
								key={message.id}
								className={`message-wrapper ${isOutgoing ? 'outgoing' : 'incoming'}`}>
								<div className="message">
									<img
										src={(isOutgoing ? authData?.avatar : message?.senderUser?.avatar || currentUser?.avatar) as string}
										className="avatar"
									/>
									<span translate="no">{message.content}</span>
								</div>

								<div className="message-footer">
									<div className="message-time">{message.time}</div>
									<div className="message-sender">
										{SENDER_TYPE_LABEL[message.senderType ?? ''] ?? ''}
									</div>
								</div>
							</div>
						);
					})}
				</React.Fragment>
			))}

			<div ref={messagesEndRef} />
		</div>
	);
};

export default MessageContentSupport;
